import { z } from "zod";

export const LibrarySchema = z.object({
  libraryID: z.string().nullish(),
  libraryName: z
    .string({ required_error: "Library Name is required" })
    .trim()
    .min(1, "Enter Library Name")
    .max(100, "Library Name must not be greater than 100 characters"),
  libraryCode: z
    .string({ required_error: "Library Code is required" })
    .trim()
    .min(1, "Enter Library Code")
    .max(10, "Library Code must not be greater than 10 characters"),
  librarianID: z.string().nullish(),
  printHeader1: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  printHeader2: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  printHeader3: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  printFooter1: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  printFooter2: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  printFooter3: z
    .string()
    .max(100, "Librarian must not be greater than 100 characters")
    .optional()
    .nullable(),
  remarks: z
    .string()
    .max(100, "Remarks must not be greater than 100 characters")
    .optional()
    .nullable(),
  issueMaxItems: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Issue Max Items")
    .transform((value) => (value === 0 ? null : value)),
  issueMaxDays: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Issue Max Days")
    .transform((value) => (value === 0 ? null : value)),
  reserveMaxItems: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Reserve Max Items")
    .transform((value) => (value === 0 ? null : value)),
  reserveMaxDays: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Reserve Max Days")
    .transform((value) => (value === 0 ? null : value)),
  phase1FineDays: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Phase 1 Fine Days")
    .transform((value) => (value === 0 ? null : value)),
  phase1FineCharge: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Phase 1 Fine Charge")
    .transform((value) => (value === 0 ? null : value)),
  phase2FineCharge: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Phase 2 Fine Charge")
    .transform((value) => (value === 0 ? null : value)),
  phase2FineDays: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Phase 2 Fine Days")
    .transform((value) => (value === 0 ? null : value)),
  defaultCharge: z.coerce
    .number({
      invalid_type_error: "Enter Number",
    })
    .nonnegative()
    .positive("Enter Default Charge")
    .transform((value) => (value === 0 ? null : value)),
  holidaySetting: z.coerce.number().nullish(),
});

export type ILibrary = z.infer<typeof LibrarySchema>;

export interface LibraryFormProps {
  library?: ILibrary;
}

export interface ILibraryGridModel {
  libraryID: string | number;
  libraryName: string;
  libraryCode: string;
  librarian: string;
  printHeader1: string;
  printHeader2: string;
  printHeader3: string;
  printFooter1: string;
  printFooter2: string;
  printFooter3: string;
  remarks: string;
  isActive: boolean;
}

export const ILibraryGridFilterSchema = z.object({
  libraryName: z.string().nullish(),
  libraryCode: z.string().nullish(),
  isShowInOPACRadio: z.coerce.number({
    message: "Select One",
    required_error: "Select One",
  }),
});

export type ILibraryGridFilter = z.infer<typeof ILibraryGridFilterSchema>;

export const GeneralSchema = z.object({
  libraryID: z.string().nullish(),
  libraryName: z
    .string({ required_error: "Library Name is required" })
    .trim()
    .min(1, "Enter Library Name")
    .max(100, "Library Name must not be greater than 100 characters"),
  libraryCode: z
    .string({ required_error: "Library Code is required" })
    .trim()
    .min(1, "Enter Library Code")
    .max(10, "Library Code must not be greater than 10 characters"),
  librarianID: z.string().nullish(),
  printHeader1: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  printHeader2: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  printHeader3: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  printFooter1: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  printFooter2: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  printFooter3: z.coerce
    .string()
    .transform((value) => (value !== "undefined" ? value : ""))
    .refine((c) => (c !== "" ? !isNaN(parseInt(c)) : true), {
      message: "Invalid number",
    })
    .refine((c) => (c !== "" ? (parseInt(c) >= 0 ? true : false) : true), {
      message: "Number Should be Greather then 0",
    })
    .nullable()
    .transform((value) => (value !== "" ? parseInt(value) : null)),
  remarks: z
    .string()
    .max(100, "Remarks must not be greater than 100 characters")
    .optional()
    .nullable(),
  isActive: z.boolean().optional(),
  isShowPatronPin: z.boolean().optional().nullable(),
  themeColor: z.string().optional().nullable(),
  fontFamily: z.string().optional().nullable(),
  libraryOpeningTime: z.string().optional().nullable(),
  libraryClosingTime: z.string().optional().nullable(),
  isAutoExitOnClosingTime: z.boolean().optional(),
  libraryLogoID: z.string().nullish(),
  libraryLogo: z
    .object({
      id: z.string().nullish(),
      url: z.string().nullish(),
    })
    .nullish(),
});
export type IGeneralSettings = z.infer<typeof GeneralSchema>;

export const CirculationSchema = z.object({
  libraryID: z.string().nullish(),
  issueMaxItems: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Issue Max Items"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),
  issueMaxDays: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Issue Max Days"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),
  reserveMaxItems: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Reserve Max Items"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),
  reserveMaxDays: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Reserve Max Days"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),
});

export type ICirculationSettings = z.infer<typeof CirculationSchema>;

export const FineSchema = z.object({
  libraryID: z.string().nullish(),
  phase1FineDays: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Phase 1 Fine Days"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),

  phase1FineCharge: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Phase 1 Fine Charge"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),

  phase2FineCharge: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Phase 2 Fine Charge"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),

  phase2FineDays: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Phase 2 Fine Days"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),

  defaultCharge: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine(
      (c) => c !== null && c !== "" && c !== undefined,
      "Enter Default Charge"
    )
    .refine((c) => !isNaN(parseInt(c)), { message: "Invalid Number" })
    .transform((c) => parseInt(c))
    .refine((c) => c >= 0, { message: "Minium value allowed is 0" }),
  isApplyFine: z.boolean().optional(),
  isAutoReceiptOnFine: z.boolean().optional(),
  isExemptFineOnHoliday: z.boolean().optional(),
  maxAllowedFineForIssue: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" || c === "" ? null : c))
    .refine((c) => c === null || !isNaN(parseFloat(c)), { message: "Invalid Number" })
    .transform((c) => (c === null ? null : parseFloat(c)))
    .refine((c) => c === null || c >= 0, { message: "Minimum value allowed is 0" })
    .optional()
    .nullable(),
  isCapFineAtMaterialCost: z.boolean().optional(),
  isRestrictIssueOnOutstandingFine: z.boolean().optional(),
});

export type IFineSettings = z.infer<typeof FineSchema>;

export const HolidaySchema = z.object({
  libraryID: z.string().nullish(),
  isSaturdayHoliday: z.boolean().optional(),
  isSundayHoliday: z.boolean().optional(),
  holidaySetting: z.coerce.number().nullish(),
});

export type IHolidaySettings = z.infer<typeof HolidaySchema>;

export const EmailSchema = z.object({
  libraryID: z.string().nullish(),
  isIssuedEmail: z.boolean().optional(),
  isReturnedEmail: z.boolean().optional(),
  isRenewEmail: z.boolean().optional(),
  isReservationReadyEmail: z.boolean().optional(),
  isReservationExpiredEmail: z.boolean().optional(),
});

export type IEmailSettings = z.infer<typeof EmailSchema>;

export const ReminderSchema = z
  .object({
    libraryID: z.string().nullish(),
    isOverDueReminder: z.boolean().optional(),
    isDueDateReminder: z.boolean().optional(),
    dueDateReminderDays: z.coerce
      .string()
      .transform((c) => (c === "null" || c === "undefined" ? null : c))

      .transform((c) => parseInt(c)),
  })
  .superRefine((data, context) => {
    if (
      data.isDueDateReminder &&
      (data.dueDateReminderDays == null || isNaN(data.dueDateReminderDays))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dueDateReminderDays"],
        message:
          "Enter Due Date Reminder Days when Due Date Reminder is enabled",
      });
    }
    if (data.isDueDateReminder && data.dueDateReminderDays == 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dueDateReminderDays"],
        message: "Reminder Days should be Greater than 0 ",
      });
    }
  });

export type IReminderSettings = z.infer<typeof ReminderSchema>;

export const MemberCardSettingsSchema = z.object({
  libraryID: z.string().nullish(),
  libraryName: z.string().optional(),
  libraryCode: z.string().optional(),
  memberCardTemplate: z.string().optional().nullable(),
  libraryLogoID: z.string().nullish(),
  libraryLogoUrl: z.string().nullish(),
  librarianSignatureID: z.string().nullish(),
  librarianSignatureUrl: z.string().nullish(),
});

export type IMemberCardSettings = z.infer<typeof MemberCardSettingsSchema>;


